/**
 * Authentication Error Classes
 *
 * Typed error hierarchy for authentication-related errors.
 * Provides consistent error handling across the auth system.
 */

/**
 * Authentication error codes
 * Normalized from backend Cognito error responses
 */
export enum AuthErrorCode {
  // Credential errors
  INCORRECT_PASSWORD = "INCORRECT_PASSWORD",
  UNKNOWN_USERNAME = "UNKNOWN_USERNAME",
  USERNAME_OR_PASSWORD_IS_INCORRECT = "USERNAME_OR_PASSWORD_IS_INCORRECT",

  // Account state errors
  UNVERIFIED_ACCOUNT = "UNVERIFIED_ACCOUNT",
  ARCHIVED_ACCOUNT = "ARCHIVED_ACCOUNT",
  LOCKED_ACCOUNT = "LOCKED_ACCOUNT",

  // Token errors
  TOKEN_EXPIRED = "TOKEN_EXPIRED",
  TOKEN_INVALID = "TOKEN_INVALID",
  TOKEN_REFRESH_FAILED = "TOKEN_REFRESH_FAILED",

  // Network errors
  NETWORK_ERROR = "NETWORK_ERROR",
  TIMEOUT_ERROR = "TIMEOUT_ERROR",

  // Generic errors
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

/**
 * Base authentication error class
 */
export class AuthError extends Error {
  constructor(
    message: string,
    public readonly code: AuthErrorCode,
    public readonly retryable: boolean = false,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = "AuthError";
    // Maintains proper stack trace in V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthError);
    }
  }

  /**
   * Check if the error is a specific type
   */
  is(code: AuthErrorCode): boolean {
    return this.code === code;
  }

  /**
   * Convert to JSON-serializable object
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      retryable: this.retryable,
    };
  }
}

/**
 * Invalid credentials error (wrong username/password)
 */
export class InvalidCredentialsError extends AuthError {
  constructor(message = "Invalid username or password", originalError?: unknown) {
    super(message, AuthErrorCode.USERNAME_OR_PASSWORD_IS_INCORRECT, false, originalError);
    this.name = "InvalidCredentialsError";
  }
}

/**
 * Token expired error
 */
export class TokenExpiredError extends AuthError {
  constructor(message = "Authentication token has expired", originalError?: unknown) {
    super(message, AuthErrorCode.TOKEN_EXPIRED, true, originalError);
    this.name = "TokenExpiredError";
  }
}

/**
 * Token refresh failed error
 */
export class TokenRefreshError extends AuthError {
  constructor(message = "Failed to refresh authentication token", originalError?: unknown) {
    super(message, AuthErrorCode.TOKEN_REFRESH_FAILED, true, originalError);
    this.name = "TokenRefreshError";
  }
}

/**
 * Account locked error
 */
export class AccountLockedError extends AuthError {
  constructor(message = "Account is locked", originalError?: unknown) {
    super(message, AuthErrorCode.LOCKED_ACCOUNT, false, originalError);
    this.name = "AccountLockedError";
  }
}

/**
 * Network error (connection issues)
 */
export class NetworkError extends AuthError {
  constructor(message = "Network connection error", originalError?: unknown) {
    super(message, AuthErrorCode.NETWORK_ERROR, true, originalError);
    this.name = "NetworkError";
  }
}

/**
 * Timeout error
 */
export class TimeoutError extends AuthError {
  constructor(message = "Request timed out", originalError?: unknown) {
    super(message, AuthErrorCode.TIMEOUT_ERROR, true, originalError);
    this.name = "TimeoutError";
  }
}

/**
 * Map backend error response to AuthError
 *
 * @param error - Backend error response or unknown error
 * @returns Typed AuthError instance
 */
export function mapToAuthError(error: unknown): AuthError {
  // Handle fetch errors
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return new NetworkError("Unable to connect to the server", error);
  }

  // Handle abort errors (timeouts)
  if (error instanceof DOMException && error.name === "AbortError") {
    return new TimeoutError("Request timed out", error);
  }

  // Handle existing AuthError instances
  if (error instanceof AuthError) {
    return error;
  }

  // Handle error responses with code
  if (typeof error === "object" && error !== null) {
    const errorObj = error as Record<string, unknown>;
    const errorCode = errorObj.code || errorObj.errorCode || errorObj.error;

    if (typeof errorCode === "string") {
      switch (errorCode) {
        case "INCORRECT_PASSWORD":
        case "UNKNOWN_USERNAME":
        case "USERNAME_OR_PASSWORD_IS_INCORRECT":
          return new InvalidCredentialsError((errorObj.message as string) || "Invalid credentials", error);
        case "LOCKED_ACCOUNT":
          return new AccountLockedError((errorObj.message as string) || "Account is locked", error);
        case "TOKEN_EXPIRED":
          return new TokenExpiredError(undefined, error);
        default:
          return new AuthError(
            (errorObj.message as string) || "Authentication error",
            AuthErrorCode.UNKNOWN_ERROR,
            false,
            error
          );
      }
    }
  }

  // Handle Error instances
  if (error instanceof Error) {
    return new AuthError(error.message, AuthErrorCode.UNKNOWN_ERROR, false, error);
  }

  // Unknown error type
  return new AuthError(
    typeof error === "string" ? error : "An unknown error occurred",
    AuthErrorCode.UNKNOWN_ERROR,
    false,
    error
  );
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof AuthError) {
    return error.retryable;
  }
  // Network errors are generally retryable
  if (error instanceof TypeError) {
    return true;
  }
  return false;
}

/**
 * urql GraphQL error structure
 */
interface UrqlGraphQLError {
  extensions?: { code?: string };
}

/**
 * urql CombinedError structure
 */
interface UrqlCombinedError {
  networkError?: Error;
  graphQLErrors?: UrqlGraphQLError[];
}

/**
 * Check if urql CombinedError is retryable
 */
export const isUrqlErrorRetryable = (error: UrqlCombinedError): boolean => {
  // Network errors are retryable
  if (error.networkError) {
    return true;
  }

  // GraphQL errors with specific codes are not retryable
  if (error.graphQLErrors) {
    const hasAuthError = error.graphQLErrors.some(
      (err: UrqlGraphQLError) => err.extensions?.code === "UNAUTHENTICATED" || err.extensions?.code === "FORBIDDEN"
    );
    return !hasAuthError;
  }

  return false;
};
